import React from "react";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Stack,
} from "@mui/material";
import { ConfirmationDialog } from "../../../../components/Dialog/ConfirmationDialog";
import { useFiltersContext } from "../FiltersContext";
import InfoIcon from "@mui/icons-material/Info";
import { useEntityNameResolver } from "../useEntityNameResolver";

export const FilterConflictDialog: React.FC = () => {
  const {
    pendingFilters,
    applyPendingFiltersWithOverride,
    cancelPendingFilters,
  } = useFiltersContext();

  const { resolvedConflicts, loading } = useEntityNameResolver(
    pendingFilters?.conflicts || []
  );

  const handleApplyOverride = () => {
    applyPendingFiltersWithOverride();
  };

  const handleCancelOverride = () => {
    cancelPendingFilters();
  };

  const getConflictDescription = () => {
    if (!pendingFilters) return "";

    return (
      <Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "rgba(255, 152, 0, 0.12)",
            p: 1.5,
            borderRadius: 1,
            border: "1px solid rgba(255, 152, 0, 0.3)",
          }}
        >
          <InfoIcon color="warning" />
          <Typography>
            <strong>Warning:</strong> The filters you're applying conflict with
            your active market definition. Applying these filters will override
            parts of your market definition.
          </Typography>
        </Box>

        <Typography pt={1}>
          The following filter conflicts were detected:
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={24} />
            <Typography ml={2}>Loading entity details...</Typography>
          </Box>
        ) : (
          <List>
            {resolvedConflicts.map((conflict, index) => (
              <ListItem
                key={index}
                sx={{
                  backgroundColor: "rgba(255, 152, 0, 0.08)",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemText
                  primary={`${conflict.type.charAt(0).toUpperCase() + conflict.type.slice(1)} Filter Conflict`}
                  secondary={
                    <Box sx={{ whiteSpace: "pre-line" }}>
                      <strong>Market definition: </strong>
                      {conflict.marketDefinition}
                      {"\n"}
                      <strong>Your filter: </strong>
                      {conflict.filterValue}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    );
  };

  return pendingFilters ? (
    <ConfirmationDialog
      open={Boolean(pendingFilters)}
      onClose={handleCancelOverride}
      onConfirm={handleApplyOverride}
      title="Filter Conflicts Detected"
      children={getConflictDescription()}
      okayButtonText="Apply Filter Override"
      cancelButtonText="Cancel"
    />
  ) : null;
};
